import { Stack, Typography } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import { ErrorDialog } from './errorDialog';

import { CopyableCode } from '../shared/copyableCode';

interface GcpSapHanaVmActionRequiredDialogProps {
  resource: InventoryResource;
  onClose: () => void;
  onRefreshResources?: () => Promise<void>;
}

export const GcpSapHanaVmActionRequiredDialog = ({
  resource,
  onClose,
  onRefreshResources,
}: GcpSapHanaVmActionRequiredDialogProps) => {
  const dal = useDAL();
  const { snackbar } = useWorkspace();

  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');

  const sourceProjectId = resource.providerAccountId;
  const bucketName = `eon-sap-${sourceProjectId}`;

  const onValidateConfiguration = async () => {
    setIsValidating(true);
    setValidationError('');

    try {
      const response = await dal.inventory.batchValidateResourceConfiguration([
        resource.id,
      ]);

      if (response.results.every((result) => result.isValid)) {
        if (onRefreshResources) {
          void onRefreshResources();
        }

        snackbar.showMessage('Configuration validated successfully.');
        onClose();
      } else {
        setValidationError(JSON.stringify(response, null, 2));
      }
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <>
      <MainDialog
        isOpen={!validationError}
        isLoading={false}
        header={{
          title: 'Complete resource configuration',
          subtitle: (
            <Typography>
              Click the button below to validate the SAP HANA configuration.
            </Typography>
          ),
          onClose,
        }}
        footer={{
          primaryButton: {
            text: 'Validate Configuration',
            onClick: onValidateConfiguration,
            'data-testid':
              'gcp-sap-hana-vm-action-required-dialog-validate-button',
          },
          secondaryButton: {
            text: 'Cancel',
            onClick: onClose,
            'data-testid':
              'gcp-sap-hana-vm-action-required-dialog-cancel-button',
          },
          submitInProgress: isValidating,
          loadingText: 'Checking configuration...',
        }}
        sx={{
          width: '40%',
          minWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'hidden',
        }}
      >
        <Stack
          className='px-[24px] py-0 overflow-y-auto'
          gap='16px'
          sx={{
            maxHeight: '50vh',
          }}
        >
          <Typography variant='body2' component='span' className='text-inherit'>
            Install the EON Agent on your SAP HANA VM using the script below to
            complete the configuration.
          </Typography>
          <CopyableCode
            code={`\
# Download and install EON Agent from GCS
gsutil cp gs://${bucketName}/agent_manager /var/lib/eon/agent_manager
gsutil cp gs://${bucketName}/agent-manager.service /etc/systemd/system/agent-manager.service
sudo chmod +x /var/lib/eon/agent_manager
`}
          />
          <Typography variant='body2' component='span' className='text-inherit'>
            Set Agent configuration after installation:
          </Typography>
          <CopyableCode
            code={`\
cat <<EOF >/var/lib/eon/agent-manager.json
{
  "auto_update": true,
  "poll_interval": "1m",
  "retry_max_attempts": 3,
  "retry_initial_delay": "1s",
  "retry_max_delay": "30s",
  "retry_multiplier": 2.0,
  "service_log_upload": true,
  "service_log_lookback": "5m",
  "cloud_provider": "gcp",
  "gcp_config": {
    "project_id": "${sourceProjectId}",
    "bucket_name": "${bucketName}"
  }
}
EOF
sudo systemctl enable agent-manager
sudo systemctl start agent-manager
`}
          />
        </Stack>
      </MainDialog>
      <ErrorDialog
        isOpen={!!validationError}
        title="Couldn't validate configuration"
        description='Check the error details and contact support if needed.'
        onClose={onClose}
        error={validationError}
      />
    </>
  );
};
